<script setup lang="ts">
import { nextTick, ref, watch } from "vue";

/**
 * Boîte de confirmation maison, à utiliser à la place de `window.confirm` :
 * celui-ci bloque le fil d'exécution, n'est pas stylable et se comporte mal
 * en application installée.
 *
 * Bâtie sur `<dialog showModal()>` : le piégeage du focus et la fermeture
 * par Échap sont natifs, comme dans `BaseSheet`.
 */

const props = withDefaults(
  defineProps<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    tone?: "danger" | "neutral";
  }>(),
  {
    confirmLabel: "Confirmer",
    cancelLabel: "Annuler",
    tone: "neutral",
  },
);

const emit = defineEmits<{
  "update:open": [value: boolean];
  confirm: [];
  cancel: [];
}>();

const dialog = ref<HTMLDialogElement | null>(null);
const cancelButton = ref<HTMLButtonElement | null>(null);

const close = (): void => emit("update:open", false);

watch(
  () => props.open,
  (open) => {
    const node = dialog.value;
    if (!node) return;
    if (open && !node.open) {
      node.showModal();
      // Sur une action destructrice, la touche Entrée ne doit jamais
      // confirmer par réflexe : le focus initial va à l'annulation.
      void nextTick(() => cancelButton.value?.focus());
    }
    if (!open && node.open) node.close();
  },
  { flush: "post" },
);

/** Échap déclenche `cancel`, comme le bouton d'annulation. */
const onCancel = (): void => {
  emit("cancel");
  close();
};

const onCancelClick = (): void => {
  emit("cancel");
  close();
};

const onConfirmClick = (): void => {
  emit("confirm");
  close();
};

const onClose = (): void => {
  if (props.open) close();
};
</script>

<template>
  <dialog
    ref="dialog"
    class="confirm-dialog"
    data-testid="confirm-dialog"
    :aria-label="title"
    @cancel="onCancel"
    @close="onClose"
  >
    <div class="confirm-dialog__panel">
      <h2>{{ title }}</h2>
      <p>{{ message }}</p>
      <div class="confirm-dialog__actions">
        <button
          ref="cancelButton"
          type="button"
          class="confirm-dialog__button confirm-dialog__button--cancel"
          data-testid="confirm-cancel"
          @click="onCancelClick"
        >
          {{ cancelLabel }}
        </button>
        <button
          type="button"
          class="confirm-dialog__button"
          data-testid="confirm-accept"
          :class="
            tone === 'danger' ? 'confirm-dialog__button--danger' : 'confirm-dialog__button--neutral'
          "
          @click="onConfirmClick"
        >
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </dialog>
</template>

<style scoped>
.confirm-dialog {
  width: min(100%, 380px);
  margin: auto;
  padding: 0;
  color: var(--color-ink);
  border: 0;
  border-radius: 24px;
  background: var(--color-ivory);
  box-shadow: var(--shadow-sheet);
  overflow: visible;
}

.confirm-dialog::backdrop {
  background: rgb(26 34 29 / 0.66);
  backdrop-filter: blur(6px);
}

.confirm-dialog[open] {
  animation: var(--animate-sheet-up);
}

.confirm-dialog__panel {
  padding: 26px 22px 22px;
  text-align: center;
}

.confirm-dialog__panel h2 {
  margin: 0 0 10px;
  font-family: var(--font-serif);
  font-size: 24px;
  font-weight: 400;
  -webkit-text-stroke: 0.12px currentColor;
}

.confirm-dialog__panel p {
  margin: 0;
  color: var(--color-muted);
  font-size: 14px;
  line-height: 1.6;
}

.confirm-dialog__actions {
  display: flex;
  gap: 10px;
  margin-top: 22px;
}

.confirm-dialog__button {
  flex: 1 1 0;
  padding: 13px;
  border: 0;
  border-radius: 11px;
  font-size: 13px;
  font-weight: 600;
}

.confirm-dialog__button--cancel {
  color: var(--color-ink);
  border: 1px solid var(--color-line);
  background: transparent;
}

.confirm-dialog__button--danger {
  color: white;
  background: var(--color-rose-strong);
}

.confirm-dialog__button--neutral {
  color: white;
  background: var(--color-olive);
}

@media (max-width: 480px) {
  .confirm-dialog {
    width: min(calc(100% - 32px), 380px);
  }
}
</style>
